import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto p-6 md:p-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">← Home</Link>
        <h1 className="text-3xl md:text-4xl font-black mt-4">Terms &amp; Conditions</h1>
        <p className="text-sm text-slate-500 mt-1">Safety Onsite Solutions, Inc. · SMS &amp; Platform Terms · Last updated February 7, 2026</p>

        <div className="mt-8 prose prose-slate max-w-none text-slate-700 space-y-6 leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-slate-900">1. About these terms</h2>
            <p>
              These Terms &amp; Conditions govern the use of the Safety Onsite
              Solutions, Inc. ("Safety Onsite Solutions," "we," "us," or
              "our") staffing operations platform, including its email and
              SMS text-message notification features. By using the platform
              or receiving SMS notifications from us, you agree to these
              terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">2. SMS program description</h2>
            <p>
              Our SMS notification program sends transactional text messages
              to designated project supervisors of our client companies to
              alert them when a contractor has submitted a weekly timesheet
              awaiting their approval. Reminder messages are sent if a
              timesheet remains unapproved after 24 or 48 hours. Every
              message contains a secure, single-use link to the timesheet
              approval page.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">3. Enrollment and consent</h2>
            <p>
              <b>Enrollment in SMS notifications is entirely voluntary and is
              never a required condition of working with Safety Onsite
              Solutions, being assigned as a supervisor on a project, or
              approving timesheets.</b> Supervisors may approve timesheets by
              the email link alone whether or not they enroll in SMS.
            </p>
            <p>Supervisors may enroll via either of two paths:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <b>Web opt-in (recommended):</b> Visit{" "}
                <Link to="/sms-signup" className="text-green-700 hover:underline">
                  app.safetyonsitesolutions.com/sms-signup
                </Link>{" "}
                and complete the enrollment form. The form requires an
                unchecked consent checkbox to be affirmatively checked before
                submission and captures a timestamped record of consent.
              </li>
              <li>
                <b>Client-assisted enrollment:</b> A client company may enroll
                their designated project supervisor after first obtaining the
                supervisor's verbal or written consent at project kickoff. Each
                enrolled supervisor is provided a printed "Supervisor
                Quick-Reference" guide that details how the SMS notification
                system works, expected message frequency, and how to opt out.
              </li>
            </ul>
            <p>
              In both cases the supervisor is fully informed of what they are
              signing up for before receiving any message and retains the
              unconditional right to opt out at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">4. Message frequency</h2>
            <p>
              Supervisors typically receive <b>1 – 2 messages per week per
              assigned project</b>, aligned with the weekly timesheet
              approval cycle. Reminder messages are sent only if a submitted
              timesheet remains unapproved.
              <b> Message and data rates may apply</b> per your mobile carrier's plan.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">5. Opt-out</h2>
            <p>
              You may opt out of SMS notifications at any time by replying
              <b> STOP</b>, <b>UNSUBSCRIBE</b>, <b>CANCEL</b>, <b>END</b>, or <b>QUIT</b> to any
              message. Upon opt-out, you will receive one confirmation
              message and no further SMS notifications will be sent to your
              number. To resubscribe, reply <b>START</b>.
            </p>
            <p>
              For help, reply <b>HELP</b> or <b>INFO</b> to any message, or
              contact us at billing@safetyonsitesolutions.com / (740) 550-9402.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">6. Supported carriers</h2>
            <p>
              Our SMS program is available on all major US mobile carriers
              including AT&amp;T, T-Mobile, Verizon Wireless, Sprint, Boost
              Mobile, U.S. Cellular, Metro by T-Mobile, Cricket Wireless,
              and Google Fi. Carriers are not liable for delayed or
              undelivered messages.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">7. Platform use</h2>
            <p>
              Contractors, supervisors, and administrators using the platform
              agree to provide accurate information (including hours worked,
              per-diem days claimed, and payment banking details) and to
              access only their own account and its authorized functions.
              Unauthorized access, tampering with timesheet data, or misuse
              of approval links is strictly prohibited.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">8. Payment terms</h2>
            <p>
              Invoices are governed by the payment terms agreed between
              Safety Onsite Solutions and each client company (default Net
              30 unless otherwise specified). Contractor payroll is issued
              on the payroll schedule communicated to each contractor.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">9. Limitation of liability</h2>
            <p>
              The platform is provided "as is." Safety Onsite Solutions
              disclaims all warranties, express or implied, to the maximum
              extent permitted by law. We are not liable for indirect,
              incidental, or consequential damages arising from platform
              downtime, delayed message delivery, or carrier-side delivery
              failures beyond our control.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">10. Privacy</h2>
            <p>
              Our collection and use of personal information is described in
              our <Link to="/privacy" className="text-green-700 hover:underline">Privacy Policy</Link>.
              <b> We do not sell, rent, or share mobile phone numbers or SMS
              consent with any third party.</b>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">11. Contact</h2>
            <p className="pl-4 border-l-2 border-slate-200">
              <b>Safety Onsite Solutions, Inc.</b><br />
              Email: billing@safetyonsitesolutions.com<br />
              Phone: (740) 550-9402
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900">12. Changes</h2>
            <p>
              We may update these Terms &amp; Conditions from time to time.
              Material changes will be reflected by updating the "Last
              updated" date. Continued use of the platform or receipt of SMS
              notifications after any such change constitutes acceptance of
              the updated terms.
            </p>
          </section>
        </div>

        <div className="mt-10 text-sm text-slate-400 border-t border-slate-200 pt-4">
          <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
          <span className="mx-2">·</span>
          <Link to="/" className="hover:text-slate-700">Home</Link>
        </div>
      </div>
    </div>
  );
}
